import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'

const asylumAssistant = {
  claims: {
    harm_type: '',
    harm_description: '',
    agent_description: '',
    motive_description: '',
    basis_of_race: false,
    basis_of_religion: false,
    basis_of_particular_social_group: false,
    basis_of_nationality: false,
    basis_of_political_opinion: false,
    narrative: '',
    party_id: null,
  },
  crimes: {
    crime: '',
    date_of_crime: null,
    date_of_crime_unknown: false,
    date_of_conviction: null,
    date_of_conviction_unknown: false,
    aggravated_felony: false,
    particularly_serious_crime: false,
    serious_non_political: false,
    narrative: '',
    party_id: null,
  },
  declarations: {
    kind: '',
    interpreter_full_name: '',
    source_language: '',
    narrative: '',
    party_id: null,
  },
  eligibilityGuides: {
    harm_rising: false,
    harm_motive: false,
    harm_agent: false,
    failure_of_state: false,
    internal_relocation: false,
    filed_within_one_year: false,
    no_crime_bars: false,
    no_national_security: false,
    no_firm_resettlement: false,
    party_id: null,
  },
  firmResettlements: {
    country_id: null,
    offer_of_permanent_residence: false,
    citizenship: false,
    other_firm_resettlement: false,
    narrative: '',
    party_id: null,
  },
  nationalSecurities: {
    putative_incident: '',
    narrative: '',
    party_id: null,
  },
  oneYearFilingDeadlines: {
    arrival_date: null,
    arrival_date_unknown: false,
    filed_more_than_one_year: false,
    does_an_exception_apply: false,
    exception_type: '',
    exception_basis: '',
    exception_end_date: null,
    exception_end_date_unknown: false,
    narrative: '',
    party_id: null,
    calculated_reasonable_time: null,
  },
  persecutorAnalysis: {
    persecutor_facts: '',
    narrative: '',
    party_id: null,
  },
  previousDenials: {
    previous_application_filed: false,
    granted: false,
    denied_by_uscis: false,
    denied_by_immigration_judge: false,
    denied_by_bia: false,
    denied_by_unknown: false,
    narrative: '',
    party_id: null,
  },
  safeThirdCountries: {
    country_id: null,
    narrative: '',
    party_id: null,
  },
}

export const ASYLUM_ASSISTANT_NAMESPACE = 'asylumAssistant'

export const AsylumAssistant = {
  namespaced: true,
  state: () => ({
    claims: [],
    crimes: [],
    declarations: [],
    eligibilityGuides: [],
    firmResettlements: [],
    nationalSecurities: [],
    oneYearFilingDeadlines: [],
    persecutorAnalysis: [],
    previousDenials: [],
    safeThirdCountries: [],
  }),
  actions: {
    async addToAsylumAssistant ({ commit }, req) {
      commit('setModel', req)
    },
    async deleteData ({ commit, state }, req) {
      if (req.id) {
        await axiosRequest.
          delete(`/parties/${req.partyId}/asylum_assistants/${req.id}`, { params: req.params }).
          then(response => {
            commit('setModel', { jsType: req.jsType, data: response.data })
          }).
          catch(handleError)
      } else {
        const entries = state[req.jsType].filter((_, index) => index !== req.index)
        commit('setModel', { jsType: req.jsType, data: { asylum_assistant: entries } })
      }
    },
    async downloadAsylumAssistantPdf (_, req) {
      return await axiosRequest.
        post(`/parties/${req.partyId}/asylum_assistant_download`, { asylum_assistant: req.params }).
        then(response => {
          if (response.data) return response.data
        }).
        catch(handleError)
    },
    async fetchData ({ commit }, req) {
      return await axiosRequest.
        get(`/parties/${req.partyId}/asylum_assistants`, { params: req.params }).
        then(response => {
          commit('setModel', { jsType: req.jsType, data: response.data })
        }).
        catch(handleError)
    },
    async saveData ({ commit }, req) {
      const { partyId, id, data, rubyType, jsType } = req
      let url = `/parties/${partyId}/asylum_assistants`
      let method = 'post'
      const requestData = {
        asylum_assistant: {
          type: rubyType,
          value: data,
        },
      }

      if (id) {
        url = `/parties/${partyId}/asylum_assistants/${id}`
        method = 'patch'
      }
      return await axiosRequest({ url, method, data: requestData }).
        then(response => {
          commit('setModel', { jsType, data: response.data })
        }).
        catch(handleError)
    },
  },
  mutations: {
    setModel: (state, value) => {
      state[value.jsType] = value.data.asylum_assistant
    },
  },
  getters: {
    getAsylumAssistant: state => state,
    getClaims: state => state.claims,
    getCrimes: state => state.crimes,
    getDeclarations: state => state.declarations,
    getEligibilityGuides: state => state.eligibilityGuides,
    getFirmResettlements: state => state.firmResettlements,
    getNationalSecurities: state => state.nationalSecurities,
    getOneYearFilingDeadlines: state => state.oneYearFilingDeadlines,
    getPersecutorAnalysis: state => state.persecutorAnalysis,
    getPreviousDenials: state => state.previousDenials,
    getSafeThirdCountries: state => state.safeThirdCountries,
    getModel: state => type => {
      return state[type]
    },
    getType: () => (type, partyId) => {
      const object = asylumAssistant[type]
      for (const attr in object) object[attr] = null

      object.party_id = partyId
      return object
    },
    currentEditedParty: (state, getters, rootState, rootGetters) => rootGetters.currentEditedParty,
    countriesOptions: (state, getters, rootState, rootGetters) => rootGetters.countriesOptions,
  },
}
